export type BatchWaitEndReason = 'cards_ready' | 'generation_completed' | 'generation_cancelled'
  | 'generation_failed' | 'left_review' | 'job_changed' | 'page_left';

export interface BatchWaitContext {
  job_id: string;
  stage?: string;
  status?: string;
  user_tier: string;
  source_slide_count?: number | null;
  requested_card_types?: string[];
  reviewed_count: number;
  accepted_count: number;
}

export type BatchWaitCapture = (event: string, properties: Record<string, unknown>, leavingPage: boolean) => void;

/** One observation per refill screen appearance, regardless of polling rerenders. */
export function createBatchWaitTracker({ capture, now, createId }: {
  capture: BatchWaitCapture;
  now: () => number;
  createId: () => string;
}) {
  let active: { id: string; start: number; visibleSince: number | null; visibleMs: number;
    initial: BatchWaitContext; latest: BatchWaitContext } | null = null;
  const emit: BatchWaitCapture = (...args) => {
    try { capture(...args); } catch { /* Analytics must never interrupt review. */ }
  };
  const finish = (reason: BatchWaitEndReason, leavingPage = false) => {
    if (!active) return;
    const wait = active;
    active = null;
    const end = now();
    const visibleMs = Math.max(0, wait.visibleMs + (wait.visibleSince === null ? 0 : end - wait.visibleSince));
    emit('review_batch_wait_ended', {
      ...wait.latest,
      $insert_id: `review-batch-wait-ended:${wait.id}`,
      wait_id: wait.id,
      wait_duration_ms: visibleMs,
      wait_duration_seconds: visibleMs / 1000,
      wall_duration_ms: Math.max(0, end - wait.start),
      end_reason: reason,
      stage_at_start: wait.initial.stage,
      reviewed_count_at_start: wait.initial.reviewed_count,
      accepted_count_at_start: wait.initial.accepted_count,
      wait_schema_version: 1,
      mock_generation: false,
    }, leavingPage);
  };
  return {
    finish,
    update(context: BatchWaitContext | null, showing: boolean, visible: boolean, reason: BatchWaitEndReason) {
      if (active && context && context.job_id !== active.latest.job_id) finish('job_changed');
      if (active && context) active.latest = { ...context };
      if (!showing || !context) { finish(reason); return; }
      const time = now();
      if (!active && visible) {
        active = { id: createId(), start: time, visibleSince: time, visibleMs: 0,
          initial: { ...context }, latest: { ...context } };
        emit('review_batch_wait_started', {
          ...context,
          $insert_id: `review-batch-wait-started:${active.id}`,
          wait_id: active.id,
          wait_schema_version: 1,
          mock_generation: false,
        }, false);
      }
      if (!active) return;
      if (!visible && active.visibleSince !== null) {
        active.visibleMs += Math.max(0, time - active.visibleSince);
        active.visibleSince = null;
      } else if (visible && active.visibleSince === null) active.visibleSince = time;
    },
  };
}
